import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../services/axiosInstance";

// ── Thunks ────────────────────────────────────────────────────────────────────

export const fetchAiWallet = createAsyncThunk(
  "aiUsage/fetchWallet",
  async (_, { rejectWithValue }) => {
    try { return (await axiosInstance.get("/ai/wallet")).data; }
    catch (e) { return rejectWithValue(e.response?.data?.error || "Failed to load wallet"); }
  }
);

// Opens Stripe's own payment page for the amount (B1.2 — 3-D Secure happens there); the tokens arrive when Stripe confirms the
// payment. Resolves with { url }; a refusal rejects with its code, for the dialog's own words.
export const topupAiWallet = createAsyncThunk(
  "aiUsage/topup",
  async (amountUsd, { rejectWithValue }) => {
    try { return (await axiosInstance.post("/billing/topup/checkout", { amountUsd })).data; }
    catch (e) { return rejectWithValue(e.response?.data?.code || "topup_failed"); }
  }
);

export const fetchAiSettings = createAsyncThunk(
  "aiUsage/fetchSettings",
  async (_, { rejectWithValue }) => {
    try { return (await axiosInstance.get("/ai/settings")).data; }
    catch (e) {
      if (e.response?.status === 403 || e.response?.status === 404) return rejectWithValue("NO_PLAN");
      return rejectWithValue(e.response?.data?.error || "Failed to load settings");
    }
  }
);

export const updateAiSettings = createAsyncThunk(
  "aiUsage/updateSettings",
  async (payload, { rejectWithValue }) => {
    try { return (await axiosInstance.put("/ai/settings", payload)).data; }
    catch (e) { return rejectWithValue(e.response?.data?.error || "Failed to save settings"); }
  }
);

export const fetchAiUsageSummary = createAsyncThunk(
  "aiUsage/fetchSummary",
  async (_, { rejectWithValue }) => {
    try { return (await axiosInstance.get("/ai/usage/summary")).data; }
    catch (e) { return rejectWithValue(e.response?.data?.error || "Failed to load summary"); }
  }
);

export const fetchAiUsage = createAsyncThunk(
  "aiUsage/fetchUsage",
  async ({ range, group }, { rejectWithValue }) => {
    try {
      const data = (await axiosInstance.get("/ai/usage", { params: { range, group } })).data;
      return { data, group };
    } catch (e) { return rejectWithValue(e.response?.data?.error || "Failed to load usage"); }
  }
);

export const fetchAiMargin = createAsyncThunk(
  "aiUsage/fetchMargin",
  async (params = {}, { rejectWithValue }) => {
    try { return (await axiosInstance.get("/ai/admin/margin", { params })).data; }
    catch (e) { return rejectWithValue(e.response?.data?.error || "Failed to load margin data"); }
  }
);

export const fetchAiFailoverStats = createAsyncThunk(
  "aiUsage/fetchFailoverStats",
  async ({ hours = 168 } = {}, { rejectWithValue }) => {
    try { return (await axiosInstance.get("/ai/failover/stats", { params: { hours } })).data; }
    catch (e) { return rejectWithValue(e.response?.data?.error || "Failed"); }
  }
);

export const fetchKnowledgeGaps = createAsyncThunk(
  "aiUsage/fetchKnowledgeGaps",
  async ({ range = "7d" } = {}, { rejectWithValue }) => {
    try { return (await axiosInstance.get("/ai/knowledge-gaps", { params: { range } })).data; }
    catch (e) { return rejectWithValue(e.response?.data?.error || "Failed"); }
  }
);

export const fetchActionsSummary = createAsyncThunk(
  "aiUsage/fetchActionsSummary",
  async ({ range = "7d" } = {}, { rejectWithValue }) => {
    try { return (await axiosInstance.get("/ai/actions/summary", { params: { range } })).data; }
    catch (e) { return rejectWithValue(e.response?.data?.error || "Failed"); }
  }
);

export const adjustAiWallet = createAsyncThunk(
  "aiUsage/adjustWallet",
  async ({ userId, amountUsd, note }, { rejectWithValue }) => {
    try { return (await axiosInstance.post(`/ai/admin/wallet/${userId}/adjust`, { amountUsd, note })).data; }
    catch (e) { return rejectWithValue(e.response?.data?.error || "Adjustment failed"); }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const aiUsageSlice = createSlice({
  name: "aiUsage",
  initialState: {
    wallet:           null,
    settings:         null,
    summary:          null,
    usageByComponent: [],
    usageByCall:      [],
    margin:           [],
    failoverStats:    null,
    knowledgeGaps:    null,
    actionsSummary:   null,

    walletStatus:         "idle",  // idle | loading | succeeded | failed
    settingsStatus:       "idle",
    summaryStatus:        "idle",
    usageStatus:          "idle",
    marginStatus:         "idle",
    failoverStatsStatus:  "idle",
    knowledgeGapsStatus:  "idle",
    actionsSummaryStatus: "idle",

    walletError:       null,
    settingsError:     null,
    summaryError:      null,
    usageError:        null,
    marginError:       null,
    topupLoading:      false,
    topupError:        null,
    settingsSaving:    false,
    settingsSaveError: null,
    adjustLoading:     false,
    adjustError:       null,
  },
  reducers: {
    clearTopupError:   (s) => { s.topupError   = null; },
    clearAdjustError:  (s) => { s.adjustError  = null; },
    clearSettingsSaveError: (s) => { s.settingsSaveError = null; },
  },
  extraReducers: (b) => {
    // Wallet
    b.addCase(fetchAiWallet.pending,   (s) => { s.walletStatus = "loading";   s.walletError = null; })
     .addCase(fetchAiWallet.fulfilled, (s, a) => { s.walletStatus = "succeeded"; s.wallet = a.payload; })
     .addCase(fetchAiWallet.rejected,  (s, a) => { s.walletStatus = "failed";    s.walletError = a.payload; });
    // Topup
    b.addCase(topupAiWallet.pending,   (s) => { s.topupLoading = true;  s.topupError = null; })
     .addCase(topupAiWallet.fulfilled, (s) => { s.topupLoading = false; })
     .addCase(topupAiWallet.rejected,  (s, a) => { s.topupLoading = false; s.topupError = a.payload; });
    // Settings
    b.addCase(fetchAiSettings.pending,   (s) => { s.settingsStatus = "loading";   s.settingsError = null; })
     .addCase(fetchAiSettings.fulfilled, (s, a) => { s.settingsStatus = "succeeded"; s.settings = a.payload; })
     .addCase(fetchAiSettings.rejected,  (s, a) => { s.settingsStatus = "failed";    s.settingsError = a.payload; });
    // Update settings
    b.addCase(updateAiSettings.pending,   (s) => { s.settingsSaving = true;  s.settingsSaveError = null; })
     .addCase(updateAiSettings.fulfilled, (s, a) => { s.settingsSaving = false; s.settings = a.payload; })
     .addCase(updateAiSettings.rejected,  (s, a) => { s.settingsSaving = false; s.settingsSaveError = a.payload; });
    // Summary
    b.addCase(fetchAiUsageSummary.pending,   (s) => { s.summaryStatus = "loading";   s.summaryError = null; })
     .addCase(fetchAiUsageSummary.fulfilled, (s, a) => {
       s.summaryStatus = "succeeded";
       const p = a.payload?.periods ?? {};
       s.summary = {
         ...a.payload,
         failoverActive: a.payload?.failoverActive ?? false,
         periodSpend: {
           today: p.today?.totalSpend ?? a.payload?.spend?.today ?? 0,
           week:  p.week?.totalSpend  ?? a.payload?.spend?.thisWeek ?? 0,
           month: p.month?.totalSpend ?? a.payload?.spend?.thisMonth ?? 0,
         },
       };
     })
     .addCase(fetchAiUsageSummary.rejected,  (s, a) => { s.summaryStatus = "failed";    s.summaryError = a.payload; });
    // Usage
    b.addCase(fetchAiUsage.pending,   (s) => { s.usageStatus = "loading"; s.usageError = null; })
     .addCase(fetchAiUsage.fulfilled, (s, a) => {
       s.usageStatus = "succeeded";
       const safeArr = (v) => (Array.isArray(v) ? v : []);
       if (a.payload.group === "component") s.usageByComponent = safeArr(a.payload.data);
       if (a.payload.group === "call")      s.usageByCall      = safeArr(a.payload.data);
     })
     .addCase(fetchAiUsage.rejected,  (s, a) => { s.usageStatus = "failed"; s.usageError = a.payload; });
    // Margin
    b.addCase(fetchAiMargin.pending,   (s) => { s.marginStatus = "loading"; s.marginError = null; })
     .addCase(fetchAiMargin.fulfilled, (s, a) => {
       s.marginStatus = "succeeded";
       s.margin = Array.isArray(a.payload) ? a.payload : (a.payload?.rows ?? []);
     })
     .addCase(fetchAiMargin.rejected,  (s, a) => { s.marginStatus = "failed";    s.marginError = a.payload; });
    // Adjust
    b.addCase(adjustAiWallet.pending,   (s) => { s.adjustLoading = true;  s.adjustError = null; })
     .addCase(adjustAiWallet.fulfilled, (s) => { s.adjustLoading = false; })
     .addCase(adjustAiWallet.rejected,  (s, a) => { s.adjustLoading = false; s.adjustError = a.payload; });
    // Failover stats
    b.addCase(fetchAiFailoverStats.pending,   (s) => { s.failoverStatsStatus = "loading"; })
     .addCase(fetchAiFailoverStats.fulfilled, (s, a) => { s.failoverStatsStatus = "succeeded"; s.failoverStats = a.payload; })
     .addCase(fetchAiFailoverStats.rejected,  (s) => { s.failoverStatsStatus = "failed"; });
    // Knowledge gaps
    b.addCase(fetchKnowledgeGaps.pending,   (s) => { s.knowledgeGapsStatus = "loading"; })
     .addCase(fetchKnowledgeGaps.fulfilled, (s, a) => { s.knowledgeGapsStatus = "succeeded"; s.knowledgeGaps = a.payload; })
     .addCase(fetchKnowledgeGaps.rejected,  (s) => { s.knowledgeGapsStatus = "failed"; });
    // Actions summary
    b.addCase(fetchActionsSummary.pending,   (s) => { s.actionsSummaryStatus = "loading"; })
     .addCase(fetchActionsSummary.fulfilled, (s, a) => { s.actionsSummaryStatus = "succeeded"; s.actionsSummary = a.payload; })
     .addCase(fetchActionsSummary.rejected,  (s) => { s.actionsSummaryStatus = "failed"; });
  },
});

export const { clearTopupError, clearAdjustError, clearSettingsSaveError } = aiUsageSlice.actions;
export default aiUsageSlice.reducer;
